import { useState } from "react";
import { SIGNING_MESSAGE } from "../constants";
import hash from "object-hash";
import { useWeb3Data } from "./useWeb3Data";
import { useGetFormData } from "./useGetFormData";
import { Form, FormService } from "../services/formService";
import { GenericResponse } from "../networking/types";
import { useRouter } from "next/router";

type FormSubmissionData = {
  data: Form | undefined;
  updateAnswer: (text: string, index: number) => void;
  validate: () => boolean;
  submitForm: () => Promise<GenericResponse>;
  submissionError?: string;
  isLoading: boolean;
  walletAddress?: string;
  isFetchingData: boolean;
};

const useFormSubmissionData = (): FormSubmissionData => {
  const formService = new FormService();
  const [submissionError, setSubmissionError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | undefined>();
  const { connectWallet, getAccount, signMessage } = useWeb3Data();
  const {
    data,
    hasError,
    isLoading: isLoadingFetchingForm,
    updateAnswer,
    validate,
  } = useGetFormData();
  const router = useRouter();
  const { id: formId } = router.query as any;

  const submitForm = async (): Promise<GenericResponse> => {
    setSubmissionError(undefined);
    setIsLoading(true);

    const isConnectedResult = await connectWallet();

    if (!isConnectedResult.success) {
      setSubmissionError(isConnectedResult.error);
      setIsLoading(false);
      return { success: false, error: isConnectedResult.error };
    }

    const accountResponse = await getAccount({
      provider: isConnectedResult.data!.provider,
    });

    if (!accountResponse.success) {
      setSubmissionError(accountResponse.error);
      setIsLoading(false);
      return { success: false, error: accountResponse.error };
    }
    const accountAddress = accountResponse.data?.account;
    setWalletAddress(accountAddress);

    const responseDataToHash = data!.questions.map((question) => ({
      id: question.id,
      body: question.body,
      answer: question.answer,
    }));

    const formHash = hash(responseDataToHash);

    const signMessageResponse = await signMessage({
      provider: isConnectedResult.data!.provider,
      message: SIGNING_MESSAGE,
      walletAddress: accountAddress!,
      contentsHash: formHash,
    });

    if (!signMessageResponse.success) {
      setSubmissionError(signMessageResponse.error);
      setIsLoading(false);
      return { success: false, error: signMessageResponse.error };
    }

    const formSubmissionResponse = await formService.submitForm({
      id: formId,
      payload: {
        walletAddress: accountAddress!,
        signature: signMessageResponse.data!.signature,
        message: SIGNING_MESSAGE,
        hashedResponses: formHash,
        responses: data!.questions.map((question) => ({
          questionId: question.id,
          answer: question.answer,
        })),
      },
    });

    if (!formSubmissionResponse.success) {
      setSubmissionError(formSubmissionResponse.error);
      setIsLoading(false);
      return { success: false, error: formSubmissionResponse.error };
    }

    return { success: true };
  };

  return {
    data,
    updateAnswer,
    submitForm,
    submissionError,
    isLoading: isLoading,
    walletAddress,
    validate,
    isFetchingData: isLoadingFetchingForm,
  };
};

export { useFormSubmissionData };
